<template>
  <div
    class="
      tw-mx-auto tw-h-screen tw-w-72
      tw-pt-10 sm:tw-pt-0
      tw-flex tw-flex-col tw-gap-6 tw-items-center tw-justify-start sm:tw-justify-center
    "
    :class="[hasPasswordAndSSOType ? 'sm:tw-w-[32rem]' : 'sm:tw-w-96']">
    <img
      :src="logoSource"
      alt="logo"
      class="tw-h-12">
    <CoreAlert
      v-if="paramErrorMessage"
      class="tw-w-full"
      data-test="param-error-alert-message"
      :message="paramErrorMessage"
      :status="AlertStatus.ERROR" />
    <CoreAlert
      v-if="shouldShowLoginConfigError"
      class="tw-w-full"
      data-test="login-configuration-error-alert-message"
      :message="t('login.errors.login_type_configuration')"
      :status="AlertStatus.ERROR" />
    <div
      v-else
      data-test="login-form-wrapper"
      class="
        tw-w-full
        tw-border tw-border-core-neutral-cold-90 tw-rounded-2xl
      ">
      <p
        class="
          headlines--headline-05 tw-text-core-primary-10
          tw-px-6 tw-py-4 tw-m-0
          tw-border-b tw-border-core-neutral-cold-90
        ">
        {{ t('common.messages.login_title', { solution: applicationName }) }}
      </p>
      <div
        class="tw-grid"
        :class="formColClasses">
        <div
          v-if="hasPasswordType || isLoginSupport"
          class="tw-flex tw-flex-col tw-p-6 tw-gap-4"
          :class="{
            'tw-border-b md:tw-border-b-0 md:tw-border-r tw-border-core-neutral-cold-90': hasPasswordAndSSOType
          }">
          <span class="headlines--headline-03 tw-text-core-primary-10 tw-text-center">{{ t('login.messages.login_with_username_password') }}</span>
          <AuthorizationCredentials
            :applicationId="applicationId"
            :isFetchingUserConfiguration="isFetchingUserConfiguration"
            @error="errorHandler"
            @loginSuccess="loginSuccessHandler" />
        </div>
        <div
          v-if="hasCorporateLogin"
          class="tw-flex tw-flex-col tw-gap-4 tw-p-4 md:tw-p-6 tw-items-center tw-justify-center">
          <span
            data-test="sso-section-title"
            class="headlines--headline-03 tw-text-core-primary-10 tw-text-center">{{ t('login.messages.signin_with') }}</span>
          <AuthorizationSSO
            v-if="shouldShowSSOLogin"
            :applicationId="applicationId"
            :configuration="ssoConfigurationValue"
            :isFetchingUserConfiguration="isFetchingUserConfiguration"
            @error="errorHandler"
            @loginSuccess="loginSuccessHandler" />
          <AuthorizationSAML
            v-if="shouldShowSAMLLogin"
            :applicationId="applicationId"
            :isFetchingUserConfiguration="isFetchingUserConfiguration"
            @error="errorHandler"
            @loginSuccess="loginSuccessHandler" />
        </div>
      </div>
    </div>
    <CoreAlert
      v-if="errorMessage"
      class="tw-w-full"
      :message="errorMessage"
      :status="AlertStatus.ERROR" />
    <div
      class="
        tw-w-full
        tw-py-4 tw-px-6
        tw-rounded-2xl tw-bg-core-primary-99
      ">
      <div class="tw-flex tw-items-center tw-gap-3">
        <SVGIcon
          colorClass="tw-fill-core-primary-50"
          iconName="MenuBook" />
        <div>
          <p class="headlines--headline-04 tw-text-core-primary-10 tw-mb-1">
            {{ t('common.messages.need_help') }}
          </p>
          <CoreLink
            :linkText="t('common.messages.user_guide')"
            dataTest="user-guide-link"
            isExternal
            to="https://doc.mlean.com" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'MLLogin' }
</script>

<script setup lang="ts">
import AuthorizationCredentials from 'frontcore/domain/authorization/view/AuthorizationCredentials.vue'
import AuthorizationSAML from 'frontcore/domain/authorization/view/AuthorizationSAML.vue'
import AuthorizationSSO from 'frontcore/domain/authorization/view/AuthorizationSSO.vue'
import CoreAlert from 'frontcore/presentation/alert/views/CoreAlert.vue'
import CoreLink from 'frontcore/views/link/CoreLink.vue'
import i18n from 'frontcore/i18n'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useLoginConfigurationStore from 'frontcore/domain/authorization/store/useLoginConfigurationStore'
import { AlertStatus } from 'frontcore/presentation/alert/domain/types'
import { computed, ref } from 'vue'
import { FactoryAllowedLoginType } from 'frontcore/domain/authorization/model/types'
import { useRoute } from 'vue-router'

type Props = {
  applicationId?: string,
  applicationName: string,
  isFetchingUserConfiguration: boolean,
  isLoginSupport?: boolean
  allowedLoginType?: FactoryAllowedLoginType
}

const props = withDefaults(defineProps<Props>(), {
  isLoginSupport: false,
  allowedLoginType: FactoryAllowedLoginType.PASSWORD,
})

type Emits = (e: 'loginSuccess') => void
const emit = defineEmits<Emits>()

const { t } = i18n.global
const route = useRoute()

const { isSAMLLogin, isSSOLogin, ssoConfigurationValue } = useLoginConfigurationStore()

const hasCorporateLogin = computed(() => {
  const hasSSOLoginType = [
    FactoryAllowedLoginType.SSO,
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
  ].includes(props.allowedLoginType)
  return (isSAMLLogin || isSSOLogin) && !props.isLoginSupport && hasSSOLoginType
})
const logoSource = `${API_URL}/media/files/branding/logo-login.png`

const loginSuccessHandler = () => {
  emit('loginSuccess')
}
const shouldShowSSOLogin = computed(() =>
  isSSOLogin && ssoConfigurationValue && !props.isLoginSupport
)
const shouldShowSAMLLogin = computed(() =>
  isSAMLLogin && !props.isLoginSupport
)
const shouldShowLoginConfigError = computed(() => {
  return [
    FactoryAllowedLoginType.SSO,
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
  ].includes(props.allowedLoginType)
    && !isSSOLogin && !isSAMLLogin && !props.isLoginSupport
})
const errorMessage = ref<string | null>(null)
const paramErrorMessage = ref<string | null>(null)

if (route.query.error) {
  const paramErrorToken = typeof(route.query.error) === 'string' ? route.query.error : ''
  paramErrorMessage.value = t(paramErrorToken)
}

const errorHandler = (message: string | null) => {
  if (message === null) {
    errorMessage.value = message
  } else {
    errorMessage.value = t(message)
  }
}

const hasPasswordAndSSOType = computed(() => {
  return props.allowedLoginType === FactoryAllowedLoginType.PASSWORD_AND_SSO && hasCorporateLogin.value
})

const hasPasswordType = computed(() => {
  return [
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
    FactoryAllowedLoginType.PASSWORD,
  ].includes(props.allowedLoginType)
})

const formColClasses = computed(() => {
  if (hasPasswordAndSSOType.value) {
    return 'tw-grid-cols-1 md:tw-grid-cols-2'
  }
  return 'tw-grid-cols-1'
})
</script>
